import { MenuItem, Select, Typography, useTheme } from "@mui/material";
import cn from "./cn";
import { useState } from "react";

export const rowPerPageOptions = [5, 10, 25, 50];

export const PaginationButtons = ({ currentPage, totalPages, handlePageChange, theme }) => {
  const pageNumbers = [];
  const createButton = (pageNum) => {
    const isActive = currentPage === pageNum;
    return (
      <button
        key={pageNum}
        onClick={() => handlePageChange(pageNum)}
        className={cn(
          'relative inline-flex items-center px-4 py-2 text-sm font-normal rounded border',
          'z-10 focus:z-20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 hover:!bg-accent-blue hover:!text-white',
          isActive && 'cursor-not-allowed',
        )}
        style={{
          backgroundColor: isActive
            ? theme.palette.info.main
            : theme.palette.primary.main,
          borderColor: theme.palette.secondary.v4,
          color: isActive ? theme.palette.text.light : theme.palette.text.main
        }}
      >
        {pageNum}
      </button>
    )
  };

  const createEllipsis = (key) => (
    <span key={key} className="relative inline-flex items-center px-1 py-2 text-sm font-normal">
      ...
    </span>
  );

  pageNumbers.push(createButton(1));

  if (currentPage > 3) {
    pageNumbers.push(createEllipsis('start-ellipsis'));
  }

  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(createButton(i));
  }

  if (currentPage < totalPages - 2) {
    pageNumbers.push(createEllipsis('end-ellipsis'));
  }

  if (totalPages > 1) {
    pageNumbers.push(createButton(totalPages));
  }

  return pageNumbers;
};

export const RowsPerPageSelector = ({ rowsPerPage, handleChangeRowsPerPage }) => {
  const theme = useTheme();
  const [selectOpen, setSelectOpen] = useState(false);

  return (
    <div className="lg:absolute left-0 flex items-center gap-2">
      <Typography
        id={'rows-per-page-label'}
        className="text-sm"
        component="label"
        htmlFor="rows-per-page-select"
      >
        Rows per page:
      </Typography>
      <Select
        value={rowsPerPage}
        onChange={handleChangeRowsPerPage}
        onOpen={() => setSelectOpen(true)}
        onClose={() => setSelectOpen(false)}
        size="small"
        id="rows-per-page-select"
        labelId={'rows-per-page-label'}
        inputProps={{
          'aria-labelledby': 'rows-per-page-label',
          'aria-expanded': selectOpen,
        }}
        sx={{
          minWidth: '72px',
          borderRadius: '6px',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.main,
          border: `1px solid ${theme.palette.secondary.v4}`,
          '& .MuiInputBase-root': {
            padding: 0,
          },
          '& .MuiSelect-select': {
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            minHeight: 'unset',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.text.main,
          },
          '&:hover': {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.text.light,
            '& .MuiSvgIcon-root': {
              color: theme.palette.text.light,
            },
          },
        }}
      >
        {rowPerPageOptions.map((option) => (
          <MenuItem
            key={option}
            value={option}
            sx={{
              color: theme.palette.text.main,
              '&.Mui-selected:hover': {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
              },
              '&:hover': {
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
              },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};
